import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ChartSyncContextType {
  selectedTimeframe: string;
  setSelectedTimeframe: (timeframe: string) => void;
  hoveredPoint: { timestamp: string; x: number } | null;
  setHoveredPoint: (point: { timestamp: string; x: number } | null) => void;
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  dragStart: { timestamp: string; x: number; price: number } | null;
  setDragStart: (point: { timestamp: string; x: number; price: number } | null) => void;
  dragEnd: { timestamp: string; x: number; price: number } | null;
  setDragEnd: (point: { timestamp: string; x: number; price: number } | null) => void;
}

const ChartSyncContext = createContext<ChartSyncContextType | undefined>(undefined);

export const ChartSyncProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Default timeframe is 1d
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('1d');
  // Track hovered point across charts
  const [hoveredPoint, setHoveredPoint] = useState<{ timestamp: string; x: number } | null>(null);
  // Track dragging state
  const [isDragging, setIsDragging] = useState<boolean>(false);
  // Track drag start and end points for price difference calculation
  const [dragStart, setDragStart] = useState<{ timestamp: string; x: number; price: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ timestamp: string; x: number; price: number } | null>(null);

  return (
    <ChartSyncContext.Provider
      value={{
        selectedTimeframe,
        setSelectedTimeframe,
        hoveredPoint,
        setHoveredPoint,
        isDragging,
        setIsDragging,
        dragStart,
        setDragStart,
        dragEnd,
        setDragEnd,
      }}
    >
      {children}
    </ChartSyncContext.Provider>
  );
};

export const useChartSync = () => {
  const context = useContext(ChartSyncContext);
  if (context === undefined) {
    throw new Error('useChartSync must be used within a ChartSyncProvider');
  }
  return context;
};